import AppComponent from '../base/app-component.ts';
import { templateParse } from '../lib/template-parse.ts';

interface StatiniStato {
  statiniEsito: 'ok' | 'parziale' | 'errore' | null;
  statiniErrore: string | null;
}

/**
 * Interpreta il campo statiniJson di una coda: l'ultima trasmissione puo' essere
 * andata a buon fine, andata a buon fine con righe scartate da Statini (parziale:
 * si puo' ritentare, il servizio rinvia solo le deleghe non ancora marcate),
 * fallita (success: false) o non essere mai avvenuta.
 * I record storici hanno una forma diversa ma conservano il flag success.
 */
function parseStatini(value: unknown): StatiniStato {
  if (!value) return { statiniEsito: null, statiniErrore: null };

  let parsed: Record<string, unknown>;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value) as Record<string, unknown>;
    } catch {
      return { statiniEsito: 'errore', statiniErrore: 'Esito trasmissione non leggibile' };
    }
  } else if (typeof value === 'object') {
    parsed = value as Record<string, unknown>;
  } else {
    return { statiniEsito: null, statiniErrore: null };
  }

  if (parsed.success === true) {
    const scartate = Array.isArray(parsed.errori) ? parsed.errori.length : 0;
    if (scartate === 0) return { statiniEsito: 'ok', statiniErrore: null };
    return {
      statiniEsito: 'parziale',
      statiniErrore: `${scartate} ${scartate === 1 ? 'delega scartata' : 'deleghe scartate'} da Statini: correggi i dati e ritenta`,
    };
  }
  const errore = typeof parsed.error === 'string' ? parsed.error : 'Trasmissione non riuscita';
  return { statiniEsito: 'errore', statiniErrore: errore };
}

export class ListaPresentazioni extends AppComponent {
  static override componentName = 'wc-lista-presentazioni';

  protected override get templateId() { return 'lista-presentazioni'; }

  private presentazioni: Record<string, unknown>[] = [];
  private loading = true;
  private error = '';

  private get statiniEnabled(): boolean {
    return this.attributeBoolean('data-statini-enabled');
  }

  private get statiniStaging(): boolean {
    return this.attributeBoolean('data-statini-staging');
  }

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      loading: this.loading,
      error: this.error,
      presentazioni: this.presentazioni,
      statiniEnabled: this.statiniEnabled,
    };
  }

  protected override afterRender(): void {
    if (this.loading) {
      this.loadData();
      return;
    }
    this.setupActions();
  }

  private async loadData(): Promise<void> {
    const result = await this.api.presentazioni.lista();
    this.loading = false;
    if (!result.ok) {
      this.error = result.error;
    } else {
      const presentazioni = result.data.presentazioni as unknown as Record<string, unknown>[];
      this.presentazioni = presentazioni.map(p => ({ ...p, ...parseStatini(p.statiniJson) }));
    }
    this.render();
  }

  private setupActions(): void {
    this.delegate.on('click', '[data-action="scarica-csv"]', (e: Event) => {
      e.preventDefault();
      const el = (e.target as HTMLElement).closest('[data-id]') as HTMLElement | null;
      const id = Number(el?.dataset.id);
      if (id) window.open(`/api/presentazioni/${id}/export-csv`, '_blank');
    });

    this.delegate.on('click', '[data-action="trasmetti-statini"]', (e: Event) => {
      e.preventDefault();
      const el = (e.target as HTMLElement).closest('[data-coda-id]') as HTMLElement | null;
      const codaId = Number(el?.dataset.codaId);
      if (codaId) this.handleTrasmetti(codaId);
    });
  }

  private async handleTrasmetti(codaId: number): Promise<void> {
    if (this.statiniStaging) {
      await Weext.alert(
        'Modalita staging: trasmissione a Statini disabilitata.',
        { title: 'Attenzione' }
      );
      return;
    }

    const ok = await Weext.confirm(
      'Trasmettere le deleghe di questa presentazione a Statini?',
      { title: 'Conferma trasmissione' }
    );
    if (!ok) return;

    const btn = this.$(`[data-action="trasmetti-statini"][data-coda-id="${codaId}"]`) as HTMLElement | null;
    if (btn) {
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('disabled');
    }

    try {
      const result = await this.api.code.trasmettiStatini(codaId);
      if (!result.ok) {
        await Weext.alert(
          `Errore trasmissione: ${result.error}`,
          { title: 'Errore' }
        );
        return;
      }
      const { caricate, errori } = result.data.result;
      const html = templateParse('lista-presentazioni-esito', { caricate, errori });
      await Weext.alert(
        html ?? `Caricate in Statini: ${caricate}`,
        { title: 'Esito trasmissione', html: true }
      );
      this.loading = true;
      this.render();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Errore sconosciuto';
      await Weext.alert(
        `Errore trasmissione: ${msg}`,
        { title: 'Errore' }
      );
    } finally {
      if (btn) {
        btn.removeAttribute('disabled');
        btn.classList.remove('disabled');
      }
    }
  }
}
